import React, { ComponentProps } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { keyframes, styled } from '@mui/material/styles'

type LoadingDotsProps = Omit<ComponentProps<typeof Container>, '$size'> & {
  size?: 'small' | 'medium' | 'large' | number
  primary?: boolean
  secondary?: boolean
}

const LoadingDots: React.FC<LoadingDotsProps> = ({
  size = 'medium',
  primary,
  secondary,
  ...props
}) => {
  return (
    <Container $size={size} {...props}>
      <Dot $size={size} $primary={primary} $secondary={secondary} $delay={0} />
      <Dot $size={size} $primary={primary} $secondary={secondary} $delay={0.2} />
      <Dot $size={size} $primary={primary} $secondary={secondary} $delay={0.4} />
    </Container>
  )
}

const bounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-8px);
  }
`

const getSizeValue = (size: string | number) => {
  if (typeof size === 'number') {
    return size
  }

  if (size === 'small') {
    return 6
  }
  else if (size === 'large') {
    return 12
  }

  return 8
}

const getGapSize = (size: string | number) => {
  const dotSize = getSizeValue(size)

  return dotSize * 1.25
}

const Container = styled('div', {
  shouldForwardProp: (prop) => !['$size'].includes(prop as string),
})<{ $size: string | number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => pxToRem(getGapSize(props.$size))};
`

const Dot = styled('span', {
  shouldForwardProp: (prop) => !['$size', '$delay', '$primary', '$secondary'].includes(prop as string),
}) <{
  $size: string | number
  $delay: number
  $primary?: boolean
  $secondary?: boolean
}>`
  width: ${props => pxToRem(getSizeValue(props.$size))};
  height: ${props => pxToRem(getSizeValue(props.$size))};
  background-color: ${props => {
    if (props.$primary) return props.theme.palette.primary.main
    if (props.$secondary) return props.theme.palette.secondary.main

    return props.theme.palette.text.primary
  }};
  border-radius: 50%;
  display: inline-block;
  animation: ${bounce} 1.4s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
`

export default LoadingDots